import {
  AbbreviationPolicy,
  ClinicianReference,
  DetailLevel,
  EmptySectionPolicy,
  FormatMode,
  NoteGenerationSettings,
  PatientReference,
  QuotationPolicy,
} from '../_interfaces/note-generation.interface';
import {
  AnalyticsEventProps,
  StyleAbbreviations,
  StyleClinicianReference,
  StyleDetailLevel,
  StyleEmptySections,
  StyleFormatMode,
  StylePatientReference,
  StyleQuotations,
  StyleSettingField,
} from '../_services/analytics.service';

export type StyleSettingsSurface = 'document_modal' | 'settings_page';

export type StyleFieldChange =
  | { field: 'detail_level'; from: StyleDetailLevel; to: StyleDetailLevel }
  | { field: 'format_mode'; from: StyleFormatMode; to: StyleFormatMode }
  | { field: 'abbreviations'; from: StyleAbbreviations; to: StyleAbbreviations }
  | { field: 'quotations'; from: StyleQuotations; to: StyleQuotations }
  | { field: 'empty_sections'; from: StyleEmptySections; to: StyleEmptySections }
  | { field: 'refer_to_clinician'; from: StyleClinicianReference; to: StyleClinicianReference }
  | { field: 'refer_to_patient'; from: StylePatientReference; to: StylePatientReference };

const MODAL_SETTING_KEYS = [
  'detailLevel',
  'formatMode',
  'abbreviationPolicy',
  'quotationPolicy',
] as const satisfies readonly (keyof NoteGenerationSettings)[];

const SETTINGS_PAGE_KEYS = [
  ...MODAL_SETTING_KEYS,
  'emptySectionPolicy',
  'referClinicianAs',
  'referPatientAs',
] as const satisfies readonly (keyof NoteGenerationSettings)[];

export function diffStyleSettings(
  before: NoteGenerationSettings,
  after: NoteGenerationSettings,
  surface: StyleSettingsSurface,
): StyleFieldChange[] {
  const keys = surface === 'document_modal' ? MODAL_SETTING_KEYS : SETTINGS_PAGE_KEYS;
  const changes: StyleFieldChange[] = [];
  for (const key of keys) {
    const change = toFieldChange(key, before[key], after[key]);
    if (change) changes.push(change);
  }
  return changes;
}

export function styleSettingsSavedProps(
  surface: StyleSettingsSurface,
  changes: StyleFieldChange[],
  modal?: { blueprintId?: string },
): AnalyticsEventProps {
  const props: AnalyticsEventProps = {
    surface,
    changed_fields: changes.map(change => change.field),
    changed_count: changes.length,
  };
  for (const change of changes) {
    assignChangedField(props, change);
  }
  if (surface === 'document_modal' && modal?.blueprintId) {
    props.blueprint_id = modal.blueprintId;
  }
  return props;
}

export function styleSettingChangedProps(
  surface: StyleSettingsSurface,
  change: StyleFieldChange,
  modal?: { blueprintId?: string },
): AnalyticsEventProps {
  const props: AnalyticsEventProps = {
    surface,
    field: change.field,
    from: change.from,
    to: change.to,
  };
  if (surface === 'document_modal' && modal?.blueprintId) {
    props.blueprint_id = modal.blueprintId;
  }
  return props;
}

function toFieldChange(
  key: keyof NoteGenerationSettings,
  before: NoteGenerationSettings[keyof NoteGenerationSettings],
  after: NoteGenerationSettings[keyof NoteGenerationSettings],
): StyleFieldChange | undefined {
  switch (key) {
    case 'detailLevel':
      return pair('detail_level', toDetailLevel(before as DetailLevel), toDetailLevel(after as DetailLevel));
    case 'formatMode':
      return pair('format_mode', toFormatMode(before as FormatMode), toFormatMode(after as FormatMode));
    case 'abbreviationPolicy':
      return pair('abbreviations', toAbbreviations(before as AbbreviationPolicy), toAbbreviations(after as AbbreviationPolicy));
    case 'quotationPolicy':
      return pair('quotations', toQuotations(before as QuotationPolicy), toQuotations(after as QuotationPolicy));
    case 'emptySectionPolicy':
      return pair('empty_sections', toEmptySections(before as EmptySectionPolicy), toEmptySections(after as EmptySectionPolicy));
    case 'referClinicianAs':
      return pair('refer_to_clinician', toClinicianReference(before as ClinicianReference), toClinicianReference(after as ClinicianReference));
    case 'referPatientAs':
      return pair('refer_to_patient', toPatientReference(before as PatientReference), toPatientReference(after as PatientReference));
    default:
      return undefined;
  }
}

function pair<TField extends StyleSettingField, TValue extends StyleFieldChange['from']>(
  field: TField,
  from: TValue | undefined,
  to: TValue | undefined,
): StyleFieldChange | undefined {
  if (!from || !to || from === to) return undefined;
  return { field, from, to } as StyleFieldChange;
}

function assignChangedField(props: AnalyticsEventProps, change: StyleFieldChange): void {
  switch (change.field) {
    case 'detail_level':
      props.detail_level_from = change.from;
      props.detail_level_to = change.to;
      break;
    case 'format_mode':
      props.format_mode_from = change.from;
      props.format_mode_to = change.to;
      break;
    case 'abbreviations':
      props.abbreviations_from = change.from;
      props.abbreviations_to = change.to;
      break;
    case 'quotations':
      props.quotations_from = change.from;
      props.quotations_to = change.to;
      break;
    case 'empty_sections':
      props.empty_sections_from = change.from;
      props.empty_sections_to = change.to;
      break;
    case 'refer_to_clinician':
      props.refer_to_clinician_from = change.from;
      props.refer_to_clinician_to = change.to;
      break;
    case 'refer_to_patient':
      props.refer_to_patient_from = change.from;
      props.refer_to_patient_to = change.to;
      break;
  }
}

function toDetailLevel(value: DetailLevel): StyleDetailLevel | undefined {
  switch (value) {
    case DetailLevel.Brief:
      return 'brief';
    case DetailLevel.Balanced:
      return 'balanced';
    case DetailLevel.Comprehensive:
      return 'comprehensive';
    default:
      return undefined;
  }
}

function toFormatMode(value: FormatMode): StyleFormatMode | undefined {
  switch (value) {
    case FormatMode.Structured:
      return 'structured';
    case FormatMode.Hybrid:
      return 'hybrid';
    case FormatMode.Prose:
      return 'prose';
    default:
      return undefined;
  }
}

function toAbbreviations(value: AbbreviationPolicy): StyleAbbreviations | undefined {
  switch (value) {
    case AbbreviationPolicy.Minimal:
      return 'off';
    case AbbreviationPolicy.Balanced:
    case AbbreviationPolicy.Liberal:
      return 'on';
    default:
      return undefined;
  }
}

function toQuotations(value: QuotationPolicy): StyleQuotations | undefined {
  switch (value) {
    case QuotationPolicy.None:
      return 'none';
    case QuotationPolicy.SalientOnly:
      return 'salient_only';
    case QuotationPolicy.Liberal:
      return 'liberal';
    default:
      return undefined;
  }
}

function toEmptySections(value: EmptySectionPolicy): StyleEmptySections | undefined {
  switch (value) {
    case EmptySectionPolicy.LeaveBlank:
      return 'leave_blank';
    case EmptySectionPolicy.Omit:
      return 'omit';
    default:
      return undefined;
  }
}

function toClinicianReference(value: ClinicianReference): StyleClinicianReference | undefined {
  switch (value) {
    case ClinicianReference.FirstPerson:
      return 'first_person';
    case ClinicianReference.Passive:
      return 'passive';
    case ClinicianReference.ThirdPerson:
      return 'third_person';
    default:
      return undefined;
  }
}

function toPatientReference(value: PatientReference): StylePatientReference | undefined {
  switch (value) {
    case PatientReference.NameIfAvailableElseThey:
      return 'name_if_available_else_they';
    case PatientReference.NameOnly:
      return 'name_only';
    case PatientReference.They:
      return 'they';
    default:
      return undefined;
  }
}
